import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type PatientRecentAppKind =
  | "meal_plan"
  | "pilates_plan"
  | "prescription"
  | "clinical_record"
  | "pilates_evolution"
  | "training_plan"
  | "heart_risk";

/** Um item por aplicativo: ícone + nome no UI; link para acesso (registro mais recente ou app + patientId). */
export interface PatientRecentAppItem {
  kind: PatientRecentAppKind;
  label: string;
  href: string;
  at: string;
}

const PILATES_EVOLUTION_SLUG = "evolucao-alunos-pilates";

function ts(
  updatedAt: string | null | undefined,
  createdAt: string | null | undefined,
  fallback?: string | null,
): string {
  return (updatedAt || createdAt || fallback || "").trim();
}

export function usePatientRecentApps(patientId: string | undefined) {
  return useQuery({
    queryKey: ["patient-recent-apps", patientId],
    enabled: Boolean(patientId),
    queryFn: async (): Promise<PatientRecentAppItem[]> => {
      if (!patientId) return [];

      const [
        { data: mealRows, error: e1 },
        { data: pilatesRows, error: e2 },
        { data: rxRows, error: e3 },
        { data: clinicalRows, error: e4 },
        { data: evoRows, error: e5 },
        { data: trainingRows, error: e6 },
        { data: trainingExtensionRows, error: e7 },
        { data: heartRiskRows, error: e8 },
        { data: heartRiskExtensionRows, error: e9 },
      ] = await Promise.all([
        supabase
          .from("meal_plans")
          .select("id, updated_at, created_at")
          .eq("patient_id", patientId)
          .order("updated_at", { ascending: false, nullsFirst: false })
          .limit(1),
        supabase
          .from("pilates_lesson_plans")
          .select("id, updated_at, created_at")
          .eq("patient_id", patientId)
          .order("updated_at", { ascending: false, nullsFirst: false })
          .limit(1),
        supabase
          .from("prescriptions")
          .select("id, updated_at, created_at")
          .eq("patient_id", patientId)
          .order("updated_at", { ascending: false, nullsFirst: false })
          .limit(1),
        supabase
          .from("clinical_records")
          .select("id, updated_at, created_at")
          .eq("patient_id", patientId)
          .order("updated_at", { ascending: false, nullsFirst: false })
          .limit(1),
        supabase
          .from("pilates_session_evolutions")
          .select("id, created_at, session_at")
          .eq("patient_id", patientId)
          .order("session_at", { ascending: false })
          .limit(1),
        supabase
          .from("training_plans")
          .select("id, created_at")
          .eq("patient_id", patientId)
          .order("created_at", { ascending: false })
          .limit(1),
        supabase
          .from("extensions")
          .select("slug")
          .in("source_path", [
            "extensions/trainingplan",
            "extensions/gymplan",
            "extension/trainingplan",
          ])
          .eq("is_active", true)
          .eq("is_suspended", false)
          .order("created_at", { ascending: false })
          .limit(1),
        supabase
          .from("patient_heart_risk_calculations")
          .select("id, calculated_at, created_at")
          .eq("patient_id", patientId)
          .order("calculated_at", { ascending: false })
          .limit(1),
        supabase
          .from("extensions")
          .select("slug")
          .in("source_path", ["extensions/heartrisk", "extension/heartrisk"])
          .eq("is_active", true)
          .eq("is_suspended", false)
          .order("created_at", { ascending: false })
          .limit(1),
      ]);

      const err = e1 || e2 || e3 || e4 || e5 || e6 || e8;
      if (err) {
        console.error("usePatientRecentApps:", err);
        throw err;
      }
      if (e7) {
        console.warn("usePatientRecentApps (training extension slug fallback):", e7);
      }
      if (e9) {
        console.warn("usePatientRecentApps (heart risk extension slug fallback):", e9);
      }

      const items: PatientRecentAppItem[] = [];

      const meal = mealRows?.[0];
      if (meal) {
        const at = ts(meal.updated_at, meal.created_at);
        if (at) {
          items.push({
            kind: "meal_plan",
            label: "Plano alimentar",
            href: `/app/aplicativo/mealplan/${meal.id}`,
            at,
          });
        }
      }

      const pilates = pilatesRows?.[0];
      if (pilates) {
        const at = ts(pilates.updated_at, pilates.created_at);
        if (at) {
          items.push({
            kind: "pilates_plan",
            label: "Planos de Aulas Pilates",
            href: `/app/aplicativo/pilatesplanner/${pilates.id}`,
            at,
          });
        }
      }

      const rx = rxRows?.[0];
      if (rx) {
        const at = ts(rx.updated_at, rx.created_at);
        if (at) {
          items.push({
            kind: "prescription",
            label: "Prescrições",
            href: `/app/aplicativo/prescription/${rx.id}`,
            at,
          });
        }
      }

      const clinical = clinicalRows?.[0];
      if (clinical) {
        const at = ts(clinical.updated_at, clinical.created_at);
        if (at) {
          items.push({
            kind: "clinical_record",
            label: "Prontuário clínico",
            href: `/app/aplicativo/clinicalrecord/${clinical.id}`,
            at,
          });
        }
      }

      const evo = evoRows?.[0];
      if (evo) {
        const at = ts(evo.session_at, evo.created_at);
        if (at) {
          items.push({
            kind: "pilates_evolution",
            label: "Evolução Alunos Pilates",
            href: `/app/aplicativos/${PILATES_EVOLUTION_SLUG}?patientId=${patientId}`,
            at,
          });
        }
      }

      const training = trainingRows?.[0];
      if (training) {
        const at = ts(training.created_at, training.created_at);
        if (at) {
          const trainingSlug = trainingExtensionRows?.[0]?.slug || "trainingplan";
          items.push({
            kind: "training_plan",
            label: "Plano de treino",
            href: `/app/aplicativos/${trainingSlug}?patientId=${patientId}`,
            at,
          });
        }
      }

      const heartRisk = heartRiskRows?.[0];
      if (heartRisk) {
        const at = ts(heartRisk.calculated_at, heartRisk.created_at);
        if (at) {
          const heartRiskSlug = heartRiskExtensionRows?.[0]?.slug || "heartrisk";
          items.push({
            kind: "heart_risk",
            label: "Risco cardiovascular",
            href: `/app/aplicativos/${heartRiskSlug}?patientId=${patientId}`,
            at,
          });
        }
      }

      items.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
      return items;
    },
  });
}
